/**
 * An account's books: the Sachkonto it is booked on, the balance the books start from, and the
 * balance the last statement printed, which the account card checks the imported rows against.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X } from "lucide-react";
import { useBookingRules, useDeleteBookingRule, useUpdateAccount } from "@/hooks/use-banking";
import { useCategories } from "@/hooks/use-documents";
import type { BankAccount } from "@/lib/api/banking";
import { parseDecimal } from "@/lib/utils/format";

const day = (value: string | undefined) => (value ?? "").slice(0, 10);
const amount = (value: number | undefined) =>
  value === undefined || value === null ? "" : String(value).replace(".", ",");

export function AccountSettingsDialog({
  account,
  onOpenChange,
}: {
  account: BankAccount | null;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={account !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        {account && (
          <AccountForm key={account.id} account={account} onDone={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function AccountForm({ account, onDone }: { account: BankAccount; onDone: () => void }) {
  const { t } = useTranslation();
  const update = useUpdateAccount();
  const [ledgerAccount, setLedgerAccount] = useState(account.ledgerAccount ?? "");
  const [openingBalance, setOpeningBalance] = useState(amount(account.openingBalance));
  const [openingDate, setOpeningDate] = useState(day(account.openingDate));
  const [statementBalance, setStatementBalance] = useState(
    account.statementDate ? amount(account.statementBalance) : "",
  );
  const [statementDate, setStatementDate] = useState(day(account.statementDate));

  async function save() {
    try {
      await update.mutateAsync({
        id: account.id,
        input: {
          ledgerAccount: ledgerAccount.trim(),
          openingBalance: parseDecimal(openingBalance) ?? 0,
          openingDate,
          statementBalance: parseDecimal(statementBalance) ?? 0,
          statementDate: statementBalance.trim() ? statementDate : "",
        },
      });
      toast.success(t("accountBooks.saved"));
      onDone();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("accountBooks.failed"));
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t("accountBooks.title", { label: account.label })}</DialogTitle>
        <DialogDescription>{t("accountBooks.hint")}</DialogDescription>
      </DialogHeader>
      <div className="grid gap-4" data-testid="account-settings-form">
        <div className="space-y-1.5">
          <Label htmlFor="account-ledger">{t("accountBooks.ledgerAccount")}</Label>
          <Input
            id="account-ledger"
            className="num sm:w-40"
            maxLength={20}
            value={ledgerAccount}
            placeholder={
              account.type === "cash" ? "2700" : account.type === "credit_card" ? "2810" : "2800"
            }
            onChange={(event) => setLedgerAccount(event.target.value)}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="account-opening-date">{t("accountBooks.openingDate")}</Label>
            <Input
              id="account-opening-date"
              type="date"
              value={openingDate}
              onChange={(event) => setOpeningDate(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="account-opening-balance">{t("accountBooks.openingBalance")}</Label>
            <Input
              id="account-opening-balance"
              inputMode="decimal"
              className="num"
              value={openingBalance}
              onChange={(event) => setOpeningBalance(event.target.value)}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">{t("accountBooks.openingHint")}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="account-statement-date">{t("accountBooks.statementDate")}</Label>
            <Input
              id="account-statement-date"
              type="date"
              value={statementDate}
              onChange={(event) => setStatementDate(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="account-statement-balance">{t("accountBooks.statementBalance")}</Label>
            <Input
              id="account-statement-balance"
              inputMode="decimal"
              className="num"
              value={statementBalance}
              onChange={(event) => setStatementBalance(event.target.value)}
            />
          </div>
        </div>
      </div>
      <AccountRules account={account} />
      <DialogFooter>
        <Button variant="outline" onClick={onDone}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={() => void save()}
          disabled={
            update.isPending ||
            (Boolean(openingBalance.trim()) && !openingDate) ||
            (Boolean(statementBalance.trim()) && !statementDate)
          }
          data-testid="save-account-settings"
        >
          {t("common.save")}
        </Button>
      </DialogFooter>
    </>
  );
}

/** The rules that book this account's recurring rows (`lib/banking/rules.ts`), to stop one. */
function AccountRules({ account }: { account: BankAccount }) {
  const { t } = useTranslation();
  const { data: rules } = useBookingRules(account.workspace);
  const { data: categories } = useCategories(account.workspace);
  const remove = useDeleteBookingRule();
  const own = (rules ?? []).filter((rule) => rule.account === account.id);
  if (own.length === 0) return null;
  const categoryName = (id?: string) => categories?.find((category) => category.id === id)?.name;

  return (
    <section className="space-y-2" data-testid="booking-rules">
      <h3 className="text-sm font-medium">{t("booking.rules")}</h3>
      <p className="text-xs text-muted-foreground">{t("booking.rulesHint")}</p>
      <ul className="divide-y rounded-md border">
        {own.map((rule) => (
          <li key={rule.id} className="flex items-center gap-2 px-3 py-2 text-sm">
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">{rule.counterparty}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {[
                  t(`booking.types.${rule.bookingType}`),
                  rule.bookingType === "category" && categoryName(rule.category),
                  rule.bookingNote,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </span>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t("booking.deleteRule", { counterparty: rule.counterparty })}
              disabled={remove.isPending}
              onClick={async () => {
                await remove.mutateAsync(rule.id);
                toast.success(t("booking.ruleDeleted"));
              }}
              data-testid="delete-rule"
            >
              <X className="size-4" />
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}
